import recoveryStyles from "../../components/dashboard/SavedPermissionRecovery.module.css";
import { useCachedQuery } from "../../hooks/useCachedQuery";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type JSX,
} from "react";
import { Link } from "react-router-dom";
import {
  useIntegrationsCatalog,
  useCatalogCategories,
  type CatalogIntegration,
  type CatalogPage,
} from "../../hooks/useIntegrationsCatalog";
import { useClaws } from "../../hooks/useClaws";
import { useIntegrations } from "../../hooks/useIntegrations";
import { FEATURE_FLAGS, flagEnabled, useAuth } from "../../hooks/useAuth";
import { Logo } from "../../components/dashboard/Logo";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { getCsrfHeader } from "../../lib/csrf";
import styles from "./IntegrationsCatalog.module.css";

type SyncState = "idle" | "syncing" | "done" | "error";

type SyncReport = { fetched: number; upserted: number; toolsUpserted: number };

// Wire shape of GET /sync/status (CatalogSyncStatusDto). Only the fields the
// button renders are typed here.
// "skipped" means another ztc-core task held the cluster-wide sync lock, so
// this one did nothing — terminal, and not an error.
type SyncStatusResponse = {
  state: "idle" | "running" | "succeeded" | "failed" | "skipped";
  fetched: number;
  upserted: number;
  toolsUpserted: number;
  errorMessage: string | null;
};

const POLL_INTERVAL_MS = 2500;
// Stop polling after this long so a wedged run doesn't spin the tab forever; the
// sync itself keeps running server-side regardless.
const POLL_TIMEOUT_MS = 12 * 60 * 1000;

const isTerminal = (s: SyncStatusResponse["state"]): boolean =>
  s === "succeeded" || s === "failed" || s === "skipped";

// Admin-only: trigger a Composio catalog sync from the dashboard instead of
// curling the endpoint. Sits top-right of the page header. The sync probes every
// toolkit (tools + API-key schemas), so it runs server-side (IP-231): POST kicks
// it off (202) and we poll GET /sync/status until it's done — the button stays
// disabled meanwhile, then the run's stats stack next to it. On mount we also
// resume polling if a run is already in flight (e.g. after a reload).
const AdminSyncButton = (): JSX.Element => {
  const [state, setState] = useState<SyncState>("idle");
  const [report, setReport] = useState<SyncReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Flipped on unmount so an in-flight poll loop never setState()s after teardown.
  const cancelledRef = useRef(false);

  const applyTerminal = useCallback((s: SyncStatusResponse): void => {
    if (s.state === "succeeded") {
      setReport({
        fetched: s.fetched,
        upserted: s.upserted,
        toolsUpserted: s.toolsUpserted,
      });
      setState("done");
    } else if (s.state === "skipped") {
      setError("Another instance is already syncing. Try again shortly.");
      setState("error");
    } else {
      setError(s.errorMessage ?? "Sync failed");
      setState("error");
    }
  }, []);

  const poll = useCallback(
    async (deadline: number): Promise<void> => {
      if (cancelledRef.current) return;
      if (Date.now() > deadline) {
        setError("Sync is taking longer than expected. Check back shortly.");
        setState("error");
        return;
      }
      try {
        const res = await fetch("/api/admin/integration-catalog/sync/status", {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`Status check failed (${res.status})`);
        const s = (await res.json()) as SyncStatusResponse;
        if (cancelledRef.current) return;
        if (isTerminal(s.state)) {
          applyTerminal(s);
          return;
        }
        window.setTimeout(() => void poll(deadline), POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        setError(err instanceof Error ? err.message : String(err));
        setState("error");
      }
    },
    [applyTerminal],
  );

  const runSync = async (): Promise<void> => {
    setState("syncing");
    setError(null);
    setReport(null);
    try {
      const res = await fetch("/api/admin/integration-catalog/sync", {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (!res.ok) throw new Error(`Sync failed (${res.status})`);
      const s = (await res.json()) as SyncStatusResponse;
      // 202 just acknowledges the kickoff; a run that already finished is applied
      // immediately, otherwise poll until it's terminal.
      if (isTerminal(s.state)) {
        applyTerminal(s);
        return;
      }
      void poll(Date.now() + POLL_TIMEOUT_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setState("error");
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    // Reflect a run already in flight (started elsewhere or before a reload) by
    // resuming polling. Best-effort: the button still works on click if this fails.
    void (async () => {
      try {
        const res = await fetch("/api/admin/integration-catalog/sync/status", {
          credentials: "include",
        });
        if (cancelledRef.current || !res.ok) return;
        const s = (await res.json()) as SyncStatusResponse;
        if (cancelledRef.current) return;
        if (s.state === "running") {
          setState("syncing");
          void poll(Date.now() + POLL_TIMEOUT_MS);
        }
      } catch {
        // ignore; the button is still usable.
      }
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, [poll]);

  return (
    <div className={styles.adminSync}>
      <button
        type="button"
        className={styles.adminSyncBtn}
        onClick={() => void runSync()}
        disabled={state === "syncing"}
      >
        {state === "syncing" ? "Syncing…" : "Sync catalog now"}
      </button>
      <div className={styles.adminSyncInfo}>
        {state === "syncing" ? (
          <span className={styles.adminSyncStat}>
            Running. This can take a few minutes.
          </span>
        ) : state === "error" ? (
          <span className={styles.adminSyncError}>{error}</span>
        ) : report ? (
          <>
            <span className={styles.adminSyncStat}>
              <strong>{report.fetched}</strong> toolkits
            </span>
            <span className={styles.adminSyncStat}>
              <strong>{report.upserted}</strong> updated
            </span>
            <span className={styles.adminSyncStat}>
              <strong>{report.toolsUpserted}</strong> tools
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
};

const PAGE_SIZE: number = 24;

const toolCountLabel = (count: number | null): string | null => {
  if (count === null || count <= 0) return null;
  return `${count.toLocaleString()} ${count === 1 ? "tool" : "tools"}`;
};

const Card = ({
  item,
  connected,
  showConnTag,
}: {
  item: CatalogIntegration;
  connected: boolean;
  showConnTag: boolean;
}): JSX.Element => {
  const tools: string | null = toolCountLabel(item.toolCount);
  // Connected supersedes Featured here: the live, user-specific state is
  // more useful at-a-glance than the editorial badge. They use the same
  // top-right slot so only one shows at a time.
  const badge: JSX.Element | null = connected ? (
    <span className={`${styles.badge} ${styles.badgeConnected}`}>
      <span className={styles.connectedDot} aria-hidden />
      Connected
    </span>
  ) : item.featured ? (
    <span className={`${styles.badge} ${styles.badgeFeatured}`}>Featured</span>
  ) : null;
  return (
    <Link
      to={`/dashboard/integrations/${item.slug}`}
      className={styles.card}
      aria-label={`Open ${item.title}`}
    >
      <div className={styles.cardBadges}>
        {badge}
        {/* Connectability tags are hidden until the connect feature flag is on
            (showConnTag), so the catalog can ship dark. A connected app is
            connectable by definition — the Connected pill already says it, so
            skip the redundant "Ready to go" tag. V49: an admin-only integration
            shows a purple "Admins only" tag to admins (the only callers who get
            item.adminOnly); regular users still see "Request access". */}
        {!showConnTag || connected ? null : item.adminOnly === true ? (
          <span className={`${styles.connTag} ${styles.connTagAdmin}`}>
            Admins only
          </span>
        ) : item.configured === false ? (
          <span className={`${styles.connTag} ${styles.connTagNo}`}>
            Request access
          </span>
        ) : (
          <span className={`${styles.connTag} ${styles.connTagOk}`}>
            Ready to go
          </span>
        )}
      </div>
      <div className={styles.cardHeader}>
        <span className={styles.cardLogo}>
          <Logo
            slug={item.slug}
            size={40}
            brandHex={item.iconBrandHex}
            logoUrl={item.ourLogoUrl ?? item.logoUrl}
          />
        </span>
        <div className={styles.cardTitleBlock}>
          <h3 className={styles.cardTitle}>{item.title}</h3>
          <div className={styles.cardCategory}>{item.category}</div>
        </div>
      </div>
      <p className={styles.cardDescription}>{item.description}</p>
      <div className={styles.cardFooter}>
        {tools !== null ? (
          <span className={styles.toolCount}>{tools}</span>
        ) : null}
        <span className={styles.cardAction} aria-hidden="true">
          Learn more &rarr;
        </span>
      </div>
    </Link>
  );
};

export const IntegrationsCatalog = (): JSX.Element => {
  const [search, setSearch] = useState<string>("");
  const [category, setCategory] = useState<string>("All");
  const [page, setPage] = useState<number>(1);

  useEffect((): void => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const { status, page: catalogPage, error, isFetching } = useIntegrationsCatalog({
    q: search,
    category,
    page,
    pageSize: PAGE_SIZE,
  });

  // Cross-reference the user's per-tenant integrations to mark which
  // catalog cards are already connected. Same tenant-resolution pattern
  // as IntegrationDetail: prefer a RUNNING claw, fall back to any owned
  // one, null = no claws (every card renders as not-connected).
  const { claws } = useClaws();
  const tenantId: string | null =
    claws.find((c) => c.status === "RUNNING")?.tenantId ??
    claws[0]?.tenantId ??
    null;
  const { integrations: tenantIntegrations } = useIntegrations(tenantId);
  const savedPolicies = useCachedQuery<{ tenantId: string | null; items: Array<{ slug: string; title: string }> }>({
    cacheResource: tenantId ? `saved-tool-permissions:${tenantId}` : null,
    queryKey: `saved-tool-permissions:${tenantId}`,
    fetcher: async signal => {
      if (!tenantId) return { tenantId, items: [] };
      const response = await fetch(`/api/integrations/${encodeURIComponent(tenantId)}/tool-permissions`, { credentials: "include", signal });
      if (response.status === 204 || response.status === 404) return { tenantId, items: [] };
      if (!response.ok) throw new Error("Could not load saved permissions");
      return { tenantId, items: await response.json() };
    },
  });

  // Ready-to-go / Request-access tags only show once the connect feature flag
  // is on, so the catalog can ship to production dark and light up later.
  const { user } = useAuth();
  const showConnTag: boolean = flagEnabled(
    user?.features,
    FEATURE_FLAGS.integrationsConnectEnabled,
  );

  const connectedSlugs = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        tenantIntegrations
          .filter((i) => i.connectionStatus === "active")
          .map((i) => i.slug),
      ),
    [tenantIntegrations],
  );

  const catalogCategories = useCatalogCategories();
  const categories: ReadonlyArray<string> = useMemo(() => {
    const set: Set<string> = new Set<string>(["All"]);
    if (catalogCategories.length > 0) {
      catalogCategories.forEach((c) => set.add(c.category));
    } else {
      catalogPage?.items.forEach((i: CatalogIntegration) => set.add(i.category));
    }
    return Array.from(set);
  }, [catalogCategories, catalogPage]);

  const totalPages: number = catalogPage
    ? Math.max(1, Math.ceil(catalogPage.total / PAGE_SIZE))
    : 1;

  return (
    <div className={styles.page}>

      <header className={styles.header}>
        <div className={styles.headerText}>
          <span className={styles.eyebrow}>// INTEGRATIONS</span>
          <h1 className={styles.title}>Integrations catalog</h1>
          <p className={styles.subtitle}>
            Browse every tool your agent can plug into. See something you need?
            Open it and register your interest — we wire up what customers ask
            for first.
          </p>
        </div>
        {user?.isAdmin ? <AdminSyncButton /> : null}
      </header>
      {savedPolicies.data?.tenantId === tenantId && savedPolicies.data?.items.length > 0 ? <section className={recoveryStyles.panel} aria-label="Saved permission settings">
        <h2>Saved permissions</h2>
        <p>Review saved rules, including integrations no longer available in the catalog.</p>
        <ul>{savedPolicies.data.items.map(item => <li key={item.slug}><Link to={`/dashboard/integrations/${encodeURIComponent(item.slug)}`}>{item.title} permissions</Link></li>)}</ul>
      </section> : null}

      <div className={styles.controls}>
        <input
          className={styles.searchInput}
          type="search"
          placeholder="Search by name or capability"
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className={styles.select}
          value={category}
          aria-label="Filter by category"
          onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
            setCategory(e.target.value);
            setPage(1);
          }}
        >
          {categories.map((c: string) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {catalogPage ? (
          <span className={styles.totalLabel}>
            {catalogPage.total.toLocaleString()} integrations
          </span>
        ) : null}
      </div>

      {status === "loading" ? (
        <CatalogGridSkeleton />
      ) : status === "error" ? (
        <div className={styles.errorState}>
          Couldn't load the catalog. {error}
        </div>
      ) : catalogPage && catalogPage.items.length === 0 ? (
        <div className={styles.emptyState}>
          No integrations match those filters.
        </div>
      ) : (
        <CatalogGrid
          catalog={catalogPage}
          connectedSlugs={connectedSlugs}
          showConnTag={showConnTag}
          isFetching={isFetching}
        />
      )}

      {catalogPage && totalPages > 1 ? (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageButton}
            onClick={(): void => setPage((p: number) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className={styles.totalLabel}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageButton}
            onClick={(): void =>
              setPage((p: number) => Math.min(totalPages, p + 1))
            }
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
};

const CatalogGridSkeleton = (): JSX.Element => (
  <div className={styles.grid} aria-busy="true" aria-label="Loading catalog">
    {Array.from({ length: PAGE_SIZE }, (_, i) => (
      <div key={i} className={styles.skeletonCard} aria-hidden>
        <Skeleton width="40px" height="40px" radius="10px" />
        <Skeleton width="55%" height="0.95rem" />
        <Skeleton width="35%" height="0.7rem" />
        <Skeleton width="90%" height="0.7rem" />
        <Skeleton width="75%" height="0.7rem" />
      </div>
    ))}
  </div>
);

const CatalogGrid = ({
  catalog,
  connectedSlugs,
  showConnTag,
  isFetching,
}: {
  catalog: CatalogPage | null;
  connectedSlugs: ReadonlySet<string>;
  showConnTag: boolean;
  isFetching: boolean;
}): JSX.Element => {
  // Partition within the current page so connected items lead. The
  // server still paginates by featured/category/title, so a connected
  // integration that landed on a later page won't bubble up to page 1 —
  // the trade-off for keeping this front-end-only per the IP-153 sketch.
  const items: ReadonlyArray<CatalogIntegration> = catalog?.items ?? [];
  const ordered: ReadonlyArray<CatalogIntegration> = useMemo(() => {
    if (connectedSlugs.size === 0) return items;
    const connected: CatalogIntegration[] = [];
    const rest: CatalogIntegration[] = [];
    for (const item of items) {
      if (connectedSlugs.has(item.slug)) connected.push(item);
      else rest.push(item);
    }
    return [...connected, ...rest];
  }, [items, connectedSlugs]);

  return (
    <div className={isFetching ? `${styles.grid} ${styles.gridFetching}` : styles.grid}>
      {ordered.map((item: CatalogIntegration) => (
        <Card
          key={item.slug}
          item={item}
          connected={connectedSlugs.has(item.slug)}
          showConnTag={showConnTag}
        />
      ))}
    </div>
  );
};
